// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {SimConfigurationService} from "./sim-configuration.service";

@Injectable({
	providedIn: "root",
})
export class SimGamepadService {
	constructor() {}

	public getGamepadClient(): GamepadClient {
		return new GamepadClient();
	}
}

type HomePosition = 1 | 2 | 3 | 4;
type Zoom = "in" | "out" | "neutral";
type Direction = "top-left" | "top" | "top-right" | "left" | "neutral" | "right" | "bottom-right" | "bottom" | "bottom-left";

/**
 * See:
 * https://beej.us/blog/data/javascript-gamepad/
 * */
export class GamepadClient {
	private primaryGamepad: Gamepad = null;

	private connected: boolean = false;
	private refreshInterval: ReturnType<typeof setInterval> = null;

	private directionVector: {x: number; y: number} = {x: 0, y: 0};
	private previousDirectionVector: {x: number; y: number} = {x: 0, y: 0};
	private previousDirection: Direction = "neutral";
	private currentDirection: Direction = "neutral";

	private zoomSpeed: number = 0;
	private previousZoomSpeed: number = 0;
	private previousZoom: Zoom = "neutral";
	private currentZoom: Zoom = "neutral";

	private currentHomePosition: HomePosition = null;
	private maxHomePositions: number = 4;

	public currentlyRequestMove: boolean = false;

	private gamepadConnectedHandler: EventListener = null;
	private gamepadDisconnectedHandler: EventListener = null;

	private readonly GamepadRefreshRate: number;
	private readonly GamepadDeadzone: number;
	private readonly GamepadChangeThreshold: number;
	private readonly GamepadFilter: string;
	private readonly GamepadInvertYAxis: boolean;

	constructor() {
		this.GamepadRefreshRate = SimConfigurationService.settings.gamepadRefreshRate;
		this.GamepadDeadzone = SimConfigurationService.settings.gamepadDeadzone;
		this.GamepadChangeThreshold = SimConfigurationService.settings.gamepadChangeThreshold;
		this.GamepadFilter = SimConfigurationService.settings.gamepadFilter;
		this.GamepadInvertYAxis = SimConfigurationService.settings.gamepadInvertYAxis;

		this.initialize();
	}

	private initialize() {
		this.gamepadConnectedHandler = this.gamepadConnected.bind(this);
		this.gamepadDisconnectedHandler = this.gamepadDisconnected.bind(this);

		window.addEventListener("gamepadconnected", this.gamepadConnectedHandler);
		window.addEventListener("gamepaddisconnected", this.gamepadDisconnectedHandler);
	}

	private scanGamepads() {
		let gamepads = navigator.getGamepads();

		if (gamepads && gamepads.length > 0) {
			if (!this.primaryGamepad) {
				// No primary gamepad assigned, look for new gamepads
				if (gamepads && gamepads.length > 0) {
					for (let gamepadIndex = 0; gamepadIndex < gamepads.length; gamepadIndex++) {
						let gamepad = gamepads[gamepadIndex];

						// Only allow axis game pads
						if (gamepad && ((this.GamepadFilter && gamepad.id.indexOf(this.GamepadFilter) >= 0) || !this.GamepadFilter)) {
							this.primaryGamepad = gamepad;
						}
					}
				}
			} else {
				// Check if the gamepad is still connected
				if (gamepads && gamepads[this.primaryGamepad.index] && gamepads[this.primaryGamepad.index].id !== this.primaryGamepad.id) {
					this.primaryGamepad = null;
				} else {
					this.primaryGamepad = gamepads[this.primaryGamepad.index];
				}
			}
		}
	}

	private gamepadConnected($event) {
		this.scanGamepads();
	}

	private gamepadDisconnected($event) {
		if (this.primaryGamepad && this.primaryGamepad.id === $event.gamepad.id) {
			this.primaryGamepad = null;
		}
	}

	public listen(
		moveUpdateCallback: (data: {direction: Direction; x: number; y: number}) => Promise<{success: boolean; direction: Direction}>,
		zoomUpdateCallback: (data: {zoom: Zoom; zoomSpeed: number}) => Promise<{success: boolean; zoom: Zoom}>,
		homePositionCallback: (data: {homePosition: HomePosition}) => Promise<{success: boolean}>,
	) {
		this.refreshInterval = setInterval(() => {
			this.scanGamepads();

			if (this.primaryGamepad) {
				this.connected = true;

				// ============================= //
				// 0 ... left = -1, right  =  1  //
				// 1 ... top  = -1, bottom =  1  //
				// 2 ... counter clockwise = -1  //
				//       clockwise         =  1  //
				// ============================= //

				this.directionVector.x = this.primaryGamepad.axes[0];

				if (!this.GamepadInvertYAxis) {
					this.directionVector.y = this.primaryGamepad.axes[1];
				} else {
					this.directionVector.y = this.primaryGamepad.axes[1] * -1;
				}

				if (this.directionVector.x < this.GamepadDeadzone * -1) {
					if (this.directionVector.y < this.GamepadDeadzone * -1) {
						this.currentDirection = "top-left";
					} else if (this.directionVector.y > this.GamepadDeadzone) {
						this.currentDirection = "bottom-left";
					} else {
						this.currentDirection = "left";
					}
				} else if (this.directionVector.x > this.GamepadDeadzone) {
					if (this.directionVector.y < this.GamepadDeadzone * -1) {
						this.currentDirection = "top-right";
					} else if (this.directionVector.y > this.GamepadDeadzone) {
						this.currentDirection = "bottom-right";
					} else {
						this.currentDirection = "right";
					}
				} else {
					if (this.directionVector.y < this.GamepadDeadzone * -1) {
						this.currentDirection = "top";
					} else if (this.directionVector.y > this.GamepadDeadzone) {
						this.currentDirection = "bottom";
					} else {
						this.currentDirection = "neutral";
					}
				}

				//console.log(gamepad.axes);

				this.zoomSpeed = this.primaryGamepad.axes[2];

				if (this.zoomSpeed < this.GamepadDeadzone * -1) {
					this.currentZoom = "in";
				} else if (this.zoomSpeed > this.GamepadDeadzone) {
					this.currentZoom = "out";
				} else {
					this.currentZoom = "neutral";
				}

				if (this.primaryGamepad.buttons && this.primaryGamepad.buttons.length > 0) {
					this.currentHomePosition = null;

					for (
						let buttonIndex = 0;
						buttonIndex < this.primaryGamepad.buttons.length && buttonIndex < this.maxHomePositions;
						buttonIndex++
					) {
						if (this.primaryGamepad.buttons[buttonIndex].pressed) {
							// The home position are one-based (not zero-based), therefore we have
							// to add 1 to the button index.
							this.currentHomePosition = <1 | 2 | 3 | 4>(buttonIndex + 1);
						}
					}
				}

				if (moveUpdateCallback) {
					if (this.currentDirection !== "neutral") {
						const deltaX = Math.abs(this.directionVector.x - this.previousDirectionVector.x);
						const deltaY = Math.abs(this.directionVector.y - this.previousDirectionVector.y);

						console.log(this.directionVector.x + ", " + this.previousDirectionVector.x);

						if (deltaX > this.GamepadChangeThreshold || deltaY > this.GamepadChangeThreshold) {
							moveUpdateCallback({
								direction: this.currentDirection,
								x: this.directionVector.x,
								y: this.directionVector.y,
							}).then((result) => {
								if (result.success) {
									// Copy values (deep copy), do not try to change this to
									// this.previousDirectionVector = this.directionVector
									// this will copy the reference of the object.
									this.previousDirectionVector.x = this.directionVector.x;
									this.previousDirectionVector.y = this.directionVector.y;

									// Copy direction
									this.previousDirection = result.direction;
								}
							});
						} else {
							// Threshold was not exceeded
						}
					} else {
						if (this.currentDirection !== this.previousDirection) {
							moveUpdateCallback({
								direction: this.currentDirection,
								x: 0,
								y: 0,
							}).then((result) => {
								this.previousDirectionVector = {x: 0, y: 0};
								this.previousDirection = result.direction;
							});
						}
					}
				}

				if (zoomUpdateCallback) {
					if (this.currentZoom !== "neutral") {
						if (Math.abs(this.previousZoomSpeed - this.zoomSpeed) > this.GamepadChangeThreshold) {
							zoomUpdateCallback({
								zoom: this.currentZoom,
								zoomSpeed: this.zoomSpeed,
							}).then((result) => {
								if (result.success) {
									this.previousZoomSpeed = this.zoomSpeed;
									this.previousZoom = result.zoom;
								}
							});
						} else {
							// Threshold was not exceeded
						}
					} else {
						if (this.currentZoom !== this.previousZoom) {
							zoomUpdateCallback({
								zoom: this.currentZoom,
								zoomSpeed: 0,
							}).then((result) => {
								if (result.success) {
									this.previousZoomSpeed = 0;
									this.previousZoom = result.zoom;
								}
							});
						}
					}
				}

				if (homePositionCallback && this.currentHomePosition) {
					homePositionCallback({
						homePosition: this.currentHomePosition,
					});
				}
			} else {
				this.connected = false;
			}
		}, this.GamepadRefreshRate);
	}

	public destroy() {
		clearInterval(this.refreshInterval);

		window.removeEventListener("gamepadconnected", this.gamepadConnectedHandler);
		window.removeEventListener("gamepaddisconnected", this.gamepadDisconnectedHandler);
	}

	public get isConnected() {
		return this.connected;
	}

	public get deviceName() {
		if (this.primaryGamepad) {
			return this.primaryGamepad.id;
		} else {
			return "No Device found";
		}
	}
}
